'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId, Fetchable } from '@morpho-org/blue-sdk';
import { isDefined, type Time } from '@morpho-org/morpho-ts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import ChartHoverCard from '@/components/charts/components/ChartHoverCard';
import { formatTimeseriesData } from '@/components/charts/helpers/highcharts';
import { defaultTimeFrame, timeFrameOptions, vaultV2DefaultTimeFrame } from '@/config';
import { isUnpricedSeries, toHistoryRange } from '@/helpers/timeseries';
import { orpc } from '@/lib/orpc/client';
export interface VaultSupplyHoverCardProps {
  address: Fetchable<Address>;
  chainId: Fetchable<ChainId>;
  assetUsd: Fetchable<bigint>;
  /** Whether the vault is a Vault V2, whose supply history comes from `vaultV2.totalAssetsHistory`. */
  isVaultV2?: boolean;
}

/** The vault's total supply in USD over the picked timeframe, charted in a hover card. */
const VaultSupplyHoverCard = t0 => {
  const $ = _c(14);
  const {
    address,
    chainId,
    assetUsd,
    isVaultV2: t1
  } = t0;
  const isVaultV2 = t1 === undefined ? false : t1;
  const [timeFrame, setTimeFrame] = useState(isVaultV2 ? vaultV2DefaultTimeFrame : defaultTimeFrame);
  let t2;
  if ($[0] !== address || $[1] !== chainId) {
    t2 = !isDefined(address) || !isDefined(chainId);
    $[0] = address;
    $[1] = chainId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isMissingIdentifiers = t2;
  const input = {
    chainId,
    address,
    range: toHistoryRange(timeFrame)
  };
  const v1Query = useQuery(orpc.vaultV1.supplyHistory.queryOptions({
    input,
    enabled: !isMissingIdentifiers && !isVaultV2
  }));
  const v2Query = useQuery(orpc.vaultV2.totalAssetsHistory.queryOptions({
    input,
    enabled: !isMissingIdentifiers && isVaultV2
  }));
  const {
    data,
    isLoading,
    isError
  } = isVaultV2 ? v2Query : v1Query;
  let points;
  let t3;
  if ($[3] !== data?.points) {
    points = data?.points ?? [];
    t3 = formatTimeseriesData(points.map(_temp));
    $[3] = data?.points;
    $[4] = points;
    $[5] = t3;
  } else {
    points = $[4];
    t3 = $[5];
  }
  const dataFormat = t3;
  let t4;
  if ($[6] !== points) {
    t4 = isUnpricedSeries(points);
    $[6] = points;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const isUnpriced = t4;
  const t5 = isError || isUnpriced;
  let t6;
  if ($[8] !== assetUsd || $[9] !== dataFormat || $[10] !== isLoading || $[11] !== t5 || $[12] !== timeFrame) {
    t6 = <ChartHoverCard data={dataFormat} timeFrameOptions={timeFrameOptions} total={assetUsd} decimals={18} loading={isLoading} error={t5} title="Total Supply" timeFrame={timeFrame} setTimeFrame={setTimeFrame} />;
    $[8] = assetUsd;
    $[9] = dataFormat;
    $[10] = isLoading;
    $[11] = t5;
    $[12] = timeFrame;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
export default VaultSupplyHoverCard;
function _temp(t0) {
  const {
    timestamp,
    totalAssetsUsd
  } = t0;
  return {
    x: timestamp,
    y: totalAssetsUsd
  };
}